import React, { useState, useEffect } from 'react';
import { useAdminStore } from '../../../../store/adminStore';
import { seoServices } from '../../../../services/seoAPIServices';
import { Button } from '../../../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../components/ui/table';
import { Badge } from '../../../../components/ui/badge';
import { Loader2, Edit, Globe, Filter, Plus, RotateCcw, ExternalLink } from 'lucide-react';
import { CategorySearch } from '../../../../components/Admin/Category/CategorySearch'; 
import LocationSearch from '../../../../components/Admin/SEO/LocationSearch';
import SeoPageForm from './SeoPageForm'; 
import SeoBulkGenerator from './SeoBulkGenerator';
import { format } from 'date-fns'; 
import api from '../../../../services/apiServices';
// --- NEW IMPORT ---
import PaginationControls from '../../../../components/Shared/PaginationControls';

const getLiveUrl = (page) => {
  const liveDomain = process.env.REACT_APP_STAGE === 'prod' ? 'www.selyst.com' : 'dev.selyst.com';
  const localeSegment = `${page.language_code || 'en'}-${(page.country_code || '').toLowerCase()}`;
  const path = page.slug || '';
  return `https://${liveDomain}/${localeSegment}${path}`;
};

const FilterBadge = ({ label, onClear }) => (
  <Badge variant="secondary" className="flex items-center gap-1 px-2 py-1 text-xs border border-gray-200 cursor-default">
    {label}
    <span onClick={onClear} className="ml-1 cursor-pointer hover:text-red-500 font-bold">×</span>
  </Badge>
);

const SeoPageManager = () => {
  const { locale, countries, setLocale, setCountries } = useAdminStore((state) => state);  

  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('BUYER'); 
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });

  // Filter Flags
  const [isFiltered, setIsFiltered] = useState(false); 
  
  const [stagedFilters, setStagedFilters] = useState({
    category: null,
    location: null,
    pageType: 'ALL'
  });
  
  const [appliedFilters, setAppliedFilters] = useState({
    category: null,
    location: null,
    pageType: null
  });

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  
  // --- Init ---
  useEffect(() => {
    if (countries.length === 0) {
      api.get('/admin/common/active-countries')
        .then(res => {
           setCountries(res.data || []);
           if (!locale && res.data && res.data.length > 0) {
               setLocale(res.data[0].country_code);
           }
        })
        .catch(err => console.error("Failed to fetch countries:", err));
    }
  }, [countries.length, setCountries, locale, setLocale]);

  // --- FETCH ---
  const fetchPages = async () => {
    setLoading(true);
    try {
      const queryParams = {
        audience: activeTab,
        country_code: locale,
        page: pagination.page,
        limit: pagination.limit,
        category_id: isFiltered ? appliedFilters.category?.id : undefined,
        location_id: isFiltered ? appliedFilters.location?.id : undefined,
        page_type: (isFiltered && appliedFilters.pageType !== 'ALL') ? appliedFilters.pageType : undefined
      };

      const res = await seoServices.listPages(queryParams);
      setPages(res.items);
      setPagination(prev => ({ ...prev, total: res.total }));
    } catch (err) {
      console.error("Error fetching pages:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (locale) fetchPages();
    // eslint-disable-next-line
  }, [pagination.page, activeTab, isFiltered, appliedFilters, locale]);

  // --- HANDLERS ---
  const handleApplyFilters = () => {
    setAppliedFilters(stagedFilters);
    setIsFiltered(true);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleResetFilters = () => {
    setStagedFilters({ category: null, location: null, pageType: 'ALL' });
    setAppliedFilters({ category: null, location: null, pageType: null });
    setIsFiltered(false); 
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleEdit = (page) => {
    setEditingPage(page);
    setIsFormOpen(true);
  };

  const handleCreate = () => {
    setEditingPage(null);
    setIsFormOpen(true);
  };

  // Helper
  const totalPages = Math.ceil(pagination.total / pagination.limit);

  return (
    <div className="h-full flex flex-col p-6 space-y-4 bg-gray-50/50">
      
      {/* HEADER */}
      <div className="flex justify-between items-center bg-white p-4 rounded-lg border shadow-sm">
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-bold text-gray-800">SEO Pages</h1>
          <div className="flex items-center gap-2 border px-3 py-1.5 rounded-md bg-gray-50">
            <Globe className="w-4 h-4 text-gray-500" />
            <Select value={locale || ''} onValueChange={setLocale}>
              <SelectTrigger className="w-[140px] h-8 border-none bg-transparent focus:ring-0 p-0">
                <SelectValue placeholder="Country" />
              </SelectTrigger>
              <SelectContent>
                {countries.map((c) => <SelectItem key={c.country_code} value={c.country_code}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => setIsBulkOpen(true)}>Bulk Generator</Button>
          <Button onClick={handleCreate} className="gap-2"><Plus className="w-4 h-4" /> Create Page</Button>
        </div>
      </div>

      {/* FILTER BAR */}
      <div className="bg-white p-4 rounded-lg border shadow-sm space-y-3">
        <div className="flex items-end gap-4 flex-wrap">
          <div className="w-[250px]">
            <label className="text-xs font-semibold text-gray-500 mb-1 block">Category</label>
            <CategorySearch 
              locale={locale} 
              onSelect={(c) => setStagedFilters(prev => ({ ...prev, category: c }))} 
              placeholder={stagedFilters.category?.name || "Filter by Category"}
            />
          </div>
          
          <div className="w-[250px]">
            <label className="text-xs font-semibold text-gray-500 mb-1 block">Location</label>
            <LocationSearch 
              countryCode={locale} 
              onSelect={(l) => setStagedFilters(prev => ({ ...prev, location: l }))}
              placeholder={stagedFilters.location?.city || "Filter by City"}
            />
          </div>

          <div className="w-[200px]">
             <label className="text-xs font-semibold text-gray-500 mb-1 block">Page Type</label>
             <Select 
                value={stagedFilters.pageType} 
                onValueChange={(val) => setStagedFilters(prev => ({ ...prev, pageType: val }))}
             >
                <SelectTrigger className="h-[38px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Types</SelectItem>
                  {/* --- EXPANDED FILTERS --- */}
                  {activeTab === 'BUYER' ? (
                    <>
                      <SelectItem value="BUYER_HUB">Buyer Hub</SelectItem>
                      <SelectItem value="ARTICLE_HUB">Article Hub (Generic)</SelectItem>       {/* NEW */}
                      <SelectItem value="BUYER_ARTICLE_HUB">Article Hub (Buyer)</SelectItem>   {/* NEW */}
                      <SelectItem value="BUYER_INDUSTRY_HUB">Industry Hub</SelectItem>
                      <SelectItem value="BUYER_GROUP_HUB">Group Collection</SelectItem>
                      <SelectItem value="BUYER_PARENT">Parent Hub</SelectItem>
                      <SelectItem value="BUYER_CHILD_NATIONAL">National Service</SelectItem>
                      <SelectItem value="BUYER_CHILD_LOCAL">Local Service</SelectItem>
                      <SelectItem value="BUYER_ARTICLE">Article</SelectItem>
                    </>
                  ) : (
                    <>
                      <SelectItem value="SELLER_INDUSTRY_HUB">Industry Hub</SelectItem>
                      <SelectItem value="SELLER_ARTICLE_HUB">Article Hub (Seller)</SelectItem> {/* NEW */}
                      <SelectItem value="SELLER_HUB">Seller Hub</SelectItem>
                      <SelectItem value="SELLER_PARENT">Parent Signup</SelectItem>
                      <SelectItem value="SELLER_LANDING_NATIONAL">Parent Signup</SelectItem>
                      <SelectItem value="SELLER_LANDING_LOCAL">Local Signup</SelectItem>
                      <SelectItem value="SELLER_ARTICLE">Article</SelectItem>
                    </>
                  )}
                </SelectContent>
             </Select>
          </div>

          <div className="flex gap-2">
            <Button onClick={handleApplyFilters} className="gap-2" variant="secondary">
                <Filter className="w-4 h-4" /> Apply
            </Button>
            
            {isFiltered && (
                <Button onClick={handleResetFilters} variant="ghost" className="text-red-600 hover:bg-red-50">
                    <RotateCcw className="w-4 h-4 mr-2" /> Reset
                </Button>
            )}
          </div>
        </div>
        
        {/* Active Filters Badges */}
        {isFiltered && (
          <div className="flex items-center gap-2 pt-2 border-t mt-2">
            <span className="text-xs font-bold text-gray-500">Filtered By:</span>
            {appliedFilters.category && <FilterBadge label={`Cat: ${appliedFilters.category.name}`} onClear={() => {
                 setStagedFilters(p => ({...p, category: null}));
                 setAppliedFilters(p => ({...p, category: null}));
            }} />}
            {appliedFilters.location && <FilterBadge label={`Loc: ${appliedFilters.location.city}`} onClear={() => {
                 setStagedFilters(p => ({...p, location: null}));
                 setAppliedFilters(p => ({...p, location: null}));
            }} />}
            {appliedFilters.pageType !== 'ALL' && <FilterBadge label={`Type: ${appliedFilters.pageType}`} onClear={() => {
                 setStagedFilters(p => ({...p, pageType: 'ALL'}));
                 setAppliedFilters(p => ({...p, pageType: 'ALL'}));
            }} />}
          </div>
        )}
      </div>

      {/* TABLE */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
        <TabsList className="w-fit">
          <TabsTrigger value="BUYER">Buyer Pages</TabsTrigger>
          <TabsTrigger value="SELLER">Seller Pages</TabsTrigger>
        </TabsList>
        
        <TabsContent value={activeTab} className="flex-1 bg-white border rounded-md mt-2 overflow-auto shadow-sm flex flex-col">
          <div className="flex-1 overflow-auto">
            <Table>
                <TableHeader className="sticky top-0 bg-gray-50 z-10 shadow-sm">
                <TableRow>
                    <TableHead className="w-[60px]">ID</TableHead>
                    <TableHead className="min-w-[250px]">Slug / Keyword</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Context</TableHead>
                    <TableHead>Updated</TableHead>
                    <TableHead className="text-right">Status</TableHead>
                    <TableHead className="w-[90px]"></TableHead>
                </TableRow>
                </TableHeader>
                <TableBody>
                {loading ? (
                    <TableRow><TableCell colSpan={7} className="h-40 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto"/></TableCell></TableRow>
                ) : pages.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="h-40 text-center text-gray-500">No pages found.</TableCell></TableRow>
                ) : (
                    pages.map((page) => (
                    <TableRow key={page.id} className="hover:bg-gray-50 group transition-colors">
                        <TableCell className="font-mono text-xs text-gray-400">{page.id}</TableCell>
                        <TableCell>
                        <div className="flex flex-col">
                            <span className="font-medium text-sm truncate max-w-[300px]" title={page.slug}>{page.slug}</span>
                            {page.keyword && (
                            <span className="text-[10px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded w-fit mt-1 border border-blue-100">
                                KW: {page.keyword}
                            </span>
                            )}
                        </div>
                        </TableCell>
                        <TableCell><Badge variant="outline" className="text-[10px]">{page.page_type}</Badge></TableCell>
                        <TableCell className="text-sm text-gray-600">
                        <div className="flex flex-col">
                            {page.category_name !== "N/A" && <span className="font-medium">{page.category_name}</span>}
                            {page.group_name && <span className="text-xs text-purple-600">Group: {page.group_name}</span>}
                            {page.industry_name && <span className="text-xs text-orange-600">Ind: {page.industry_name}</span>}
                            {page.city_name && <span className="text-xs text-gray-400">📍 {page.city_name}</span>}
                        </div>
                        </TableCell>
                        <TableCell className="text-xs text-gray-500">
                            {page.last_mod ? format(new Date(page.last_mod), 'dd MMM, HH:mm') : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                        {page.is_live ? (
                            <Badge className="bg-green-500 hover:bg-green-600">Live</Badge>
                        ) : (
                            <Badge variant="secondary">Draft</Badge>
                        )}
                        </TableCell>
                        <TableCell>
                        <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button variant="ghost" size="icon" asChild title="View live page">
                                <a href={getLiveUrl(page)} target="_blank" rel="noopener noreferrer">
                                    <ExternalLink className="w-4 h-4 text-gray-500" />
                                </a>
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleEdit(page)} title="Edit page">
                                <Edit className="w-4 h-4 text-gray-500" />
                            </Button>
                        </div>
                        </TableCell>
                    </TableRow>
                    ))
                )}
                </TableBody>
            </Table>
          </div>

          {/* PAGINATION */}
          {pages.length > 0 && (
            <div className="p-4 border-t bg-gray-50 flex justify-between items-center">
                <p className="text-xs text-gray-500">
                    Showing {pages.length} of {pagination.total} records
                </p>
                <PaginationControls 
                    currentPage={pagination.page}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                />
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* MODALS */}
      <SeoPageForm 
        open={isFormOpen} 
        onOpenChange={setIsFormOpen} 
        initialData={editingPage}
        audience={activeTab}
        onSuccess={() => { setIsFormOpen(false); fetchPages(); }}
      />
      
      <SeoBulkGenerator 
        open={isBulkOpen} 
        onOpenChange={setIsBulkOpen} 
        audience={activeTab}
      />
    </div>
  );
};

export default SeoPageManager;